import { cva } from 'class-variance-authority';

interface TooltipWrapperProps {
  children: React.ReactNode;
  arrow?: 'top' | 'bottom' | 'left' | 'right';
  variant?: 'light' | 'dark';
  textStyle?: 'default' | 'emphasized';
  className?: string;
}

const arrowLightStyles: Record<NonNullable<TooltipWrapperProps['arrow']>, string> = {
  bottom:
    'absolute -bottom-[6.5px] left-1/2 -translate-x-1/2 w-3 h-3 -rotate-45 bg-white border-l border-b border-gray-200 shadow-[0px_0px_6px_0px_rgba(233,236,239,0.36)]',
  top: 'absolute -top-[6.5px] left-1/2 -translate-x-1/2 w-3 h-3 rotate-[135deg] bg-white border-l border-b border-gray-200 shadow-[0px_0px_6px_0px_rgba(233,236,239,0.36)]',
  left: 'absolute -left-[6.5px] top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 bg-white border-l border-b border-gray-200 shadow-[0px_0px_6px_0px_rgba(233,236,239,0.36)]',
  right:
    'absolute -right-[6.5px] top-1/2 -translate-y-1/2 w-3 h-3 -rotate-[135deg] bg-white border-l border-b border-gray-200 shadow-[0px_0px_6px_0px_rgba(233,236,239,0.36)]',
};

const arrowDarkStyles: Record<NonNullable<TooltipWrapperProps['arrow']>, string> = {
  bottom: 'absolute -bottom-[6.5px] left-1/2 -translate-x-1/2 w-3 h-3 -rotate-45 bg-blue-300',
  top: 'absolute -top-[6.5px] left-1/2 -translate-x-1/2 w-3 h-3 rotate-[135deg] bg-blue-300',
  left: 'absolute -left-[6.5px] top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 bg-blue-300',
  right: 'absolute -right-[6.5px] top-1/2 -translate-y-1/2 w-3 h-3 -rotate-[135deg] bg-blue-300',
};

const textStyles = cva('text-body-small font-medium', {
  variants: {
    textStyle: {
      default: 'text-black',
      emphasized: 'text-blue-900 font-medium',
    },
    isDark: {
      true: '!text-white',
      false: '',
    },
  },
  defaultVariants: {
    textStyle: 'default',
    isDark: false,
  },
});

export const TooltipWrapper = ({
  children,
  arrow = 'bottom',
  variant = 'light',
  textStyle = 'default',
  className,
}: TooltipWrapperProps) => {
  const isDark = variant === 'dark';
  const arrowStyles = isDark ? arrowDarkStyles : arrowLightStyles;
  return (
    <div className="relative inline-block">
      <div
        className={`relative whitespace-pre-line max-md:whitespace-normal max-md:max-w-[calc(100vw-4rem)] text-sm px-3 py-1.5 rounded-md ${
          isDark
            ? 'bg-blue-300 text-white'
            : 'bg-white text-gray-800 border border-gray-200 shadow-[0px_0px_6px_0px_rgba(233,236,239,0.36)]'
        } ${className ?? ''}`}
      >
        <span className={textStyles({ textStyle, isDark })}>{children}</span>
        <div className={arrowStyles[arrow]}></div>
      </div>
    </div>
  );
};
